import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { UserContext } from '../context/UserContext';

function clampInt(value, min, max) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function mod360(deg) {
  const m = deg % 360;
  return m < 0 ? m + 360 : m;
}

export default function Gluecksrad() {
  const { user } = useContext(UserContext);
  const isOwner = Array.isArray(user?.roles) && user.roles.includes('owner');

  const wheelRef = useRef(null);
  const [wheelPx, setWheelPx] = useState(0);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [spinning, setSpinning] = useState(false);
  const [spinDeg, setSpinDeg] = useState(0);
  const [result, setResult] = useState(null);
  const [pendingResult, setPendingResult] = useState(null);

  const [claiming, setClaiming] = useState(false);
  const [claimMsg, setClaimMsg] = useState('');
  const [claimErr, setClaimErr] = useState('');

  const [editItems, setEditItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');

  const [spinCost, setSpinCost] = useState(0);
  const [editSpinCost, setEditSpinCost] = useState(0);
  const [costMsg, setCostMsg] = useState('');

  const [freeSpins, setFreeSpins] = useState({ daily: 0, bonus: 0, total: 0 });

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [cfgRes, meRes] = await Promise.all([
          api.get('/lucky-wheel/config'),
          api.get('/lucky-wheel/me'),
        ]);
        const data = cfgRes.data;
        const me = meRes.data;
        if (!mounted) return;
        const list = Array.isArray(data?.items) ? data.items : [];
        const sc = Number(data?.spinCost) || 0;
        setItems(list);
        setEditItems(list.map((it) => ({ ...it })));
        setSpinCost(sc);
        setEditSpinCost(sc);

        const fs = me?.freeSpins;
        setFreeSpins({
          daily: Number(fs?.daily) || 0,
          bonus: Number(fs?.bonus) || 0,
          total: Number(fs?.total) || 0,
        });
      } catch (e) {
        if (!mounted) return;
        setLoadError(e?.response?.data?.error || e.message || 'Fehler beim Laden');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const el = wheelRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const size = Math.min(rect.width || 0, rect.height || 0);
      if (Number.isFinite(size) && size > 0) setWheelPx(size);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      try {
        ro.disconnect();
      } catch {}
      window.removeEventListener('resize', update);
    };
  }, []);

  const wheelBackground = useMemo(() => {
    const n = Math.max(0, items.length);
    if (n === 0) return 'conic-gradient(from -90deg, rgba(255,255,255,0.15), rgba(255,255,255,0.15))';

    const period = 360 / n;
    const line = `repeating-conic-gradient(from -90deg, rgba(255,255,255,0.22) 0deg 1deg, transparent 1deg ${period}deg)`;

    const parts = [];
    for (let i = 0; i < n; i++) {
      const a0 = (i * 360) / n;
      const a1 = ((i + 1) * 360) / n;
      const fill = i % 2 === 0 ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.08)';
      parts.push(`${fill} ${a0}deg ${a1}deg`);
    }
    const fill = `conic-gradient(from -90deg, ${parts.join(', ')})`;
    return `${line}, ${fill}`;
  }, [items]);

  const sliceAngle = useMemo(() => {
    const n = items.length;
    return n > 0 ? 360 / n : 0;
  }, [items.length]);

  const wheelSize = 'min(640px, 92vw)';
  const spinDurationSec = 9.6;

  const labelDistancePx = useMemo(() => {
    if (!wheelPx) return 140;
    return Math.max(70, wheelPx * 0.31);
  }, [wheelPx]);

  const labelFontPx = useMemo(() => {
    if (!wheelPx) return 11;
    return Math.max(10, Math.min(14, Math.floor(wheelPx / 52)));
  }, [wheelPx]);

  const nextSpinNotice = useMemo(() => {
    const total = Number(freeSpins?.total) || 0;
    const daily = Number(freeSpins?.daily) || 0;
    const bonus = Number(freeSpins?.bonus) || 0;
    const cost = Number(spinCost) || 0;
    if (total > 0) {
      const kind = daily > 0 ? 'täglich' : (bonus > 0 ? 'Bonus' : 'Gratis');
      return {
        headline: 'Nächster Dreh: GRATIS',
        detail: kind === 'Bonus' ? `Quelle: ${kind} (Bonus: ${bonus})` : `Quelle: ${kind}`,
      };
    }
    if (cost <= 0) return { headline: 'Nächster Dreh: GRATIS', detail: 'Preis: 0 Wochenpunkte' };
    return { headline: `Nächster Dreh: kostet ${cost} Wochenpunkte`, detail: '' };
  }, [freeSpins, spinCost]);

  const spin = async () => {
    if (spinning) return;
    if (!items.length) return;
    setSaveMsg('');
    setSaveErr('');
    setResult(null);
    setPendingResult(null);
    setClaimMsg('');
    setClaimErr('');
    setCostMsg('');
    setSpinning(true);

    try {
      const { data } = await api.post('/lucky-wheel/spin');
      const idx = clampInt(data?.index, 0, items.length - 1);
      const turns = clampInt(data?.turns, 1, 50);
      const spinId = typeof data?.spinId === 'string' ? data.spinId : '';
      const sc = Number(data?.spinCost) || 0;

      const usedFree = typeof data?.usedFreeSpin === 'string' ? data.usedFreeSpin : null;
      const costApplied = Number(data?.costApplied) || 0;
      if (usedFree) setCostMsg('Gratisdrehung verwendet');
      else if (costApplied > 0) setCostMsg(`Preis: -${costApplied} Wochenpunkte`);

      const fs = data?.freeSpins;
      if (fs) {
        setFreeSpins({ daily: Number(fs?.daily) || 0, bonus: Number(fs?.bonus) || 0, total: Number(fs?.total) || 0 });
      }

      const centerAngle = idx * sliceAngle + sliceAngle / 2;
      const desiredMod = mod360(360 - centerAngle);
      const currentMod = mod360(spinDeg);
      const delta = mod360(desiredMod - currentMod);
      const totalDelta = turns * 360 + delta;

      const nextDeg = spinDeg + totalDelta;
      setSpinDeg(nextDeg);
      setPendingResult({
        index: idx,
        label: data?.label || items[idx]?.label || `Feld ${idx + 1}`,
        spinId,
      });
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Drehen fehlgeschlagen');
      setSpinning(false);
    }
  };

  const onWheelTransitionEnd = async (e) => {
    if (e?.propertyName && e.propertyName !== 'transform') return;
    setSpinning(false);

    const finalResult = pendingResult;
    setResult((prev) => prev || finalResult);
    setPendingResult(null);

    const spinId = typeof finalResult?.spinId === 'string' ? finalResult.spinId : '';
    if (!spinId) return;

    setClaiming(true);
    setClaimMsg('');
    setClaimErr('');
    try {
      const { data } = await api.post('/lucky-wheel/claim', { spinId });
      const action = data?.action;
      if (action?.type === 'points') {
        const n = Number(action?.pointsDelta) || 0;
        const sign = n > 0 ? '+' : '';
        setClaimMsg(`Punkte: ${sign}${n}`);
      } else if (action?.type === 'freeSpins') {
        const n = Number(action?.freeSpinsDelta) || 0;
        setClaimMsg(`Gratisdrehungen: +${n}`);
      } else if (action?.type === 'prize') {
        setClaimMsg(`Sachpreis gewonnen: ${action?.label || ''}`);
      } else {
        setClaimMsg('Niete');
      }

      const fs = data?.freeSpins;
      if (fs) {
        setFreeSpins({ daily: Number(fs?.daily) || 0, bonus: Number(fs?.bonus) || 0, total: Number(fs?.total) || 0 });
      }
    } catch (err) {
      setClaimErr(err?.response?.data?.error || err.message || 'Aktion fehlgeschlagen');
    } finally {
      setClaiming(false);
    }
  };

  const saveWeights = async () => {
    if (!isOwner) return;
    setSaving(true);
    setSaveMsg('');
    setSaveErr('');
    try {
      const payload = {
        spinCost: clampInt(editSpinCost, 0, 1000000),
        items: (editItems || []).map((it, i) => ({
          label: String(it?.label || `Feld ${i + 1}`).trim(),
          weight: clampInt(it?.weight, 0, 1000000),
          actionType: it?.actionType === 'points'
            ? 'points'
            : (it?.actionType === 'freeSpins'
              ? 'freeSpins'
              : (it?.actionType === 'prize' ? 'prize' : 'none')),
          pointsDelta: it?.actionType === 'points' ? Math.trunc(Number(it?.pointsDelta) || 0) : null,
          freeSpinsDelta: it?.actionType === 'freeSpins' ? clampInt(it?.freeSpinsDelta, 0, 1000000) : null,
        })),
      };
      const { data } = await api.put('/lucky-wheel/config', payload);
      const list = Array.isArray(data?.items) ? data.items : payload.items;
      const sc = Number(data?.spinCost);
      setItems(list);
      setEditItems(list.map((it) => ({ ...it })));
      if (Number.isFinite(sc)) {
        setSpinCost(sc);
        setEditSpinCost(sc);
      }
      setSaveMsg(data?.message || 'Gespeichert');
    } catch (e) {
      setSaveErr(e?.response?.data?.error || e.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="home-page" style={{ position: 'relative' }}>
      {/* Hero header */}
      <section style={{ width: '100vw', margin: 0, padding: 0, position: 'relative', left: '-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>
          Glücksrad
        </h2>
        <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 760 }}>
          Drehe täglich am Rad und erhalte zufällige Gewinne.
        </p>
      </section>

      {/* Centered content */}
      <section
        style={{
          width: '100%',
          maxWidth: 900,
          margin: '0 auto',
          padding: '0 16px 40px 16px',
          color: '#fff',
        }}
      >
        {loading && <div style={{ color: '#ccc' }}>Lade Glücksrad…</div>}
        {!loading && loadError && <div style={{ color: '#ffb3b3' }}>{loadError}</div>}

        {!loading && !loadError && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              {/* Pointer */}
              <div
                aria-hidden
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '12px solid transparent',
                  borderRight: '12px solid transparent',
                  borderTop: '18px solid rgba(255,255,255,0.9)',
                  filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))',
                  marginBottom: -6,
                }}
              />

              {/* Wheel */}
              <div
                ref={wheelRef}
                style={{
                  width: wheelSize,
                  height: wheelSize,
                  borderRadius: '50%',
                  background: wheelBackground,
                  border: '1px solid rgba(255,255,255,0.18)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                  position: 'relative',
                  transform: `rotate(${spinDeg}deg)`,
                  transition: spinning ? `transform ${spinDurationSec}s cubic-bezier(0.12, 0.85, 0.12, 1)` : 'none',
                  userSelect: 'none',
                }}
                onTransitionEnd={onWheelTransitionEnd}
              >
                {/* Slice labels */}
                {items.map((it, i) => {
                  const raw = String(it?.label || '').trim();
                  const label = raw.length > 19 ? `${raw.slice(0, 19)}…` : raw;
                  const angle = i * sliceAngle + sliceAngle / 2;
                  const rot = angle - 90;
                  const dist = labelDistancePx;
                  return (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: `translate(-50%, -50%) rotate(${rot}deg)`,
                        color: 'rgba(255,255,255,0.95)',
                        textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                        pointerEvents: 'none',
                        fontSize: labelFontPx,
                        lineHeight: 1.1,
                        opacity: 0.95,
                      }}
                      title={raw}
                    >
                      <div
                        style={{
                          transform: `translateX(${dist}px)`,
                          transformOrigin: 'left center',
                          whiteSpace: 'nowrap',
                          fontWeight: 800,
                          fontSize: Math.max(10, labelFontPx),
                          opacity: 0.95,
                          textAlign: 'left',
                        }}
                      >
                        {label || `Feld ${i + 1}`}
                      </div>
                    </div>
                  );
                })}

                {/* Center cap */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      width: 184,
                      height: 184,
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.35)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 700,
                      color: '#fff',
                      fontSize: 28,
                    }}
                  >
                    ⭐
                  </div>
                </div>
              </div>

              {/* Prominent next-spin notice */}
              <div
                style={{
                  width: '100%',
                  maxWidth: 520,
                  marginTop: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.22)',
                  background: 'rgba(0,0,0,0.35)',
                  boxShadow: '0 10px 24px rgba(0,0,0,0.35)',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 900, color: 'rgba(255,255,255,0.98)', letterSpacing: 0.2 }}>
                  {nextSpinNotice.headline}
                </div>
                {nextSpinNotice.detail && (
                  <div style={{ marginTop: 4, fontSize: 12, color: '#ccc' }}>{nextSpinNotice.detail}</div>
                )}
                <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>
                  Hinweis: Die tägliche Gratisdrehung verfällt, wenn du sie heute nicht nutzt.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button onClick={spin} disabled={spinning || !items.length}>
                  {spinning ? 'Dreht…' : 'Drehen'}
                </button>
                {result && (
                  <div style={{ color: '#fff' }}>
                    Ergebnis: <b>{result.label}</b>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 6, color: '#ccc', fontSize: 12, textAlign: 'center' }}>
                Gratisdrehungen: <b>{Number(freeSpins?.total) || 0}</b> (täglich: {Number(freeSpins?.daily) || 0}, Bonus: {Number(freeSpins?.bonus) || 0})
              </div>

              {(claiming || claimMsg || claimErr) && (
                <div style={{ marginTop: 6, color: '#ccc', fontSize: 12, textAlign: 'center' }}>
                  {claiming && 'Aktion wird angewendet…'}
                  {!claiming && claimMsg && <span style={{ color: '#c8ffc8' }}>{claimMsg}</span>}
                  {!claiming && claimErr && <span style={{ color: '#ffb3b3' }}>{claimErr}</span>}
                </div>
              )}

              {spinning && costMsg && (
                <div style={{ marginTop: 6, color: '#c8ffc8', fontSize: 12, textAlign: 'center' }}>{costMsg}</div>
              )}

              <div style={{ width: '100%', maxWidth: 520, color: '#ccc', fontSize: 12, textAlign: 'center' }}>
                Das Ergebnis wird serverseitig zufällig ermittelt.
              </div>
            </div>

            {/* Owner: probability config */}
            {isOwner && (
              <div style={{ marginTop: 28, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                <h3 style={{ margin: '0 0 10px' }}>Owner: Wahrscheinlichkeiten</h3>
                <div style={{ color: '#ccc', fontSize: 12, marginBottom: 12 }}>
                  Gewichte &gt; 0 erhöhen die Wahrscheinlichkeit des jeweiligen Felds (relative Gewichtung).
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, maxWidth: 740 }}>
                  <div style={{ color: '#ccc', fontSize: 12, minWidth: 170 }}>Preis pro Dreh (Punkte):</div>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={Number.isFinite(Number(editSpinCost)) ? editSpinCost : 0}
                    onChange={(e) => setEditSpinCost(e.target.value)}
                    style={{ width: 140 }}
                  />
                  <div style={{ color: '#999', fontSize: 12 }}>
                    Aktuell: {Number(spinCost) || 0}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 110px 140px', gap: 8, maxWidth: 740 }}>
                  {(editItems || []).map((it, i) => (
                    <div key={i} style={{ display: 'contents' }}>
                      <div>
                        <input
                          type="text"
                          value={typeof it?.label === 'string' ? it.label : ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditItems((prev) => {
                              const next = [...(prev || [])];
                              next[i] = { ...next[i], label: v };
                              return next;
                            });
                          }}
                          placeholder={`Feld ${i + 1}`}
                          style={{ width: '100%' }}
                        />
                      </div>

                      <div>
                        <select
                          value={it?.actionType === 'points' ? 'points' : (it?.actionType === 'freeSpins' ? 'freeSpins' : (it?.actionType === 'prize' ? 'prize' : 'none'))}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditItems((prev) => {
                              const next = [...(prev || [])];
                              next[i] = {
                                ...next[i],
                                actionType: v === 'points' ? 'points' : (v === 'freeSpins' ? 'freeSpins' : (v === 'prize' ? 'prize' : 'none')),
                                pointsDelta: v === 'points' ? (Number(next[i]?.pointsDelta) || 0) : null,
                                freeSpinsDelta: v === 'freeSpins' ? (Number(next[i]?.freeSpinsDelta) || 1) : null,
                              };
                              return next;
                            });
                          }}
                          style={{ width: 90 }}
                        >
                          <option value="none">Niete</option>
                          <option value="points">Punkte</option>
                          <option value="freeSpins">Gratis</option>
                          <option value="prize">Sachpreis</option>
                        </select>
                      </div>

                      <div>
                        <input
                          type="number"
                          step={1}
                          value={
                            it?.actionType === 'freeSpins'
                              ? (Number.isFinite(Number(it?.freeSpinsDelta)) ? it.freeSpinsDelta : 1)
                              : (Number.isFinite(Number(it?.pointsDelta)) ? it.pointsDelta : 0)
                          }
                          disabled={it?.actionType !== 'points' && it?.actionType !== 'freeSpins'}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditItems((prev) => {
                              const next = [...(prev || [])];
                              if (next[i]?.actionType === 'freeSpins') next[i] = { ...next[i], freeSpinsDelta: v };
                              else next[i] = { ...next[i], pointsDelta: v };
                              return next;
                            });
                          }}
                          style={{ width: 110 }}
                        />
                      </div>

                      <div>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={Number.isFinite(Number(it?.weight)) ? it.weight : 0}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditItems((prev) => {
                              const next = [...(prev || [])];
                              next[i] = { ...next[i], weight: v };
                              return next;
                            });
                          }}
                          style={{ width: 120 }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button onClick={saveWeights} disabled={saving}>
                    {saving ? 'Speichert…' : 'Wahrscheinlichkeiten speichern'}
                  </button>
                  {saveMsg && <div style={{ color: '#c8ffc8' }}>{saveMsg}</div>}
                  {saveErr && <div style={{ color: '#ffb3b3' }}>{saveErr}</div>}
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
